<template>
    <Head>
        <title>Управление валютами и сетями</title>
    </Head>

    <authenticated-layout :notifications="notifications">
        <template #header>
            <NavigationSettings />
        </template>

        <div class="flex flex-col gap-8 p-8">
            <div class="flex flex-row items-baseline justify-between">
                <h1 class="text-2xl font-bold">Управление валютами и сетями</h1>
                <div class="flex flex-row gap-2">
                    <v-button color="green" @click="openCurrencyModal(null)">Создать</v-button>
                    <v-button-link color="yellow" :href="route('admin.currencies.clear')">
                        Сбросить кэш
                    </v-button-link>
                </div>
            </div>

            <div class="flex flex-col bg-white rounded p-8">
                <div class="flex flex-row gap-8 mb-4 items-center">
                    <v-toggle label-left="MainNet" label="TestNet" v-model="testnet"/>
                    <div class="w-96">
                        <v-select label="Sort" v-model="sortBy">
                            <option value="currency-asc">Currency (A-Z)</option>
                            <option value="currency-desc">Currency (Z-A)</option>
                            <option value="orderIndex-asc">Order Index (0-9)</option>
                            <option value="orderIndex-desc">Order Index (9-0)</option>
                        </v-select>
                    </div>
                </div>

                <div>
                    <div class="grid grid-cols-11 font-bold text-gray-700 p-2 gap-2" >
                        <span>Order Index</span>
                        <select class="border-0 p-0 m-0 font-bold text-gray-700" v-model="filters.isActive">
                            <option :value="null">Is Active</option>
                            <option :value="true">Is Active = true</option>
                            <option :value="false">Is Active = false</option>
                        </select>
                        <span>Currency (Alias)</span>
                        <select class="border-0 p-0 m-0 font-bold text-gray-700" v-model="filters.inBlockchain">
                            <option :value="null">In Blockchain</option>
                            <option :value="true">In Blockchain = true</option>
                            <option :value="false">In Blockchain = false</option>
                        </select>
                        <select class="border-0 p-0 m-0 font-bold text-gray-700" v-model="filters.isFiat">
                            <option :value="null">Is Fiat</option>
                            <option :value="true">Is Fiat = true</option>
                            <option :value="false">Is Fiat = false</option>
                        </select>
                        <span>Self Blockchain</span>
                        <span class="col-span-3">Networks</span>
                        <span>Created At (UTC)</span>
                    </div>

                    <div>
                        <div
                            v-for="currency in currenciesFiltered"
                            :key="currency.id"
                            class="grid grid-cols-11 p-2 gap-2 text-sm border-t hover:bg-gray-100 items-center"
                        >
                            <span>{{ currency.orderIndex }}</span>
                            <span :class="currency.isActive ? 'font-bold' : 'text-gray-500'">{{ currency.isActive }}</span>
                            <span>{{ currency.currency }} ({{ currency.alias }})</span>
                            <span :class="currency.inBlockchain ? 'font-bold' : 'text-gray-500'">{{ currency.inBlockchain }}</span>
                            <span :class="currency.isFiat ? 'font-bold' : 'text-gray-500'">{{ currency.isFiat }}</span>
                            <span :class="currency.selfBlockchain ? 'font-bold' : 'text-gray-500'">{{ currency.selfBlockchain }}</span>
                            <div class="col-span-3 flex flex-col">
                                <div
                                    class="cursor-pointer text-blue-700 flex flex-row gap-2"
                                    v-for="net of currency.networks"
                                >
                                    <span class="cursor-pointer text-red-700"
                                          @click="deleteLink(currency, net)"
                                    >x</span>
                                    <span class="cursor-pointer text-blue-700"
                                          @click="openLinkModal(currency, net)">{{ net.name }} ({{ net.alias }})</span>
                                </div>
                                <span
                                    class="cursor-pointer text-green-700"
                                    @click="openLinkModal(currency, null)"
                                >+</span>
                            </div>
                            <span>{{ moment(currency.createdAt).utc().format('L LTS') }}</span>
                            <div class="flex flex-row gap-2 items-start">
                                <v-button color="red" @click="deleteCurrency(currency)" type="button">Delete</v-button>
                                <v-button color="green" @click="openCurrencyModal(currency)" type="button">Edit</v-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <v-modal :showing="showCurrencyModal" @close="showCurrencyModal = false">
                <template v-slot:header>
                    {{ modalCurrency.id ? `Edit currency id: ${modalCurrency.id}` : 'Create currency' }}
                </template>
                <div class="grid grid-cols-3 gap-2">
                    <v-input label="Currency Name" class="col-span-full" v-model="modalCurrency.currency" />
                    <v-input label="Alias" class="col-span-full" v-model="modalCurrency.alias" />
                    <v-input label="Order Index" type="number" min="-2147483648" max="2147483647" v-model="modalCurrency.orderIndex" />
                    <v-input label="Exchange Equal" v-model="modalCurrency.exchangeEqual" />
                    <v-input label="Rounding" v-model="modalCurrency.rounding" />
                    <v-input label="Url Info" class="col-span-full" v-model="modalCurrency.urlInfo" />
                    <v-toggle label="Is Active" v-model="modalCurrency.isActive" />
                    <v-toggle label="In Blockchain" v-model="modalCurrency.inBlockchain" />
                    <v-toggle label="Is Fiat" v-model="modalCurrency.isFiat" />
                    <v-toggle label="Is Base Currency" v-model="modalCurrency.isBaseCurrency" />
                    <v-toggle label="System" v-model="modalCurrency.system" />
                    <v-toggle label="Stablecoin" v-model="modalCurrency.stablecoin" />
                    <v-toggle label="selfBlockchain" v-model="modalCurrency.selfBlockchain" />
                    <v-toggle label="Allow Deposit" v-model="modalCurrency.allowDeposit" />
                    <v-toggle label="Allow Withdraw" v-model="modalCurrency.allowWithdraw" />
                    <v-toggle label="Allow Exchange" v-model="modalCurrency.allowExchange" />
                    <v-toggle label="Allow Invoice" v-model="modalCurrency.allowInvoice" />
                    <v-toggle label="Allow Payout" v-model="modalCurrency.allowPayout" />
                    <v-toggle label="Allow Business" v-model="modalCurrency.allowBusiness" />
                    <v-toggle label="Allow Accounts" v-model="modalCurrency.allowAccounts" />
                </div>
                <template v-slot:footer>
                    <v-button type="button" color="green" :disabled="loading" @click="saveCurrency">Save</v-button>
                </template>
            </v-modal>


            <v-modal :showing="showLinkModal" @close="showLinkModal = false">
                <template v-slot:header>
                    Edit link {{ modalCurrency.currency }}
                </template>
                <div class="grid grid-cols-3 gap-2">
                    <v-select label="network" class="col-span-full" v-model="modalLink.networkId" :disabled="!modalLink.isCreate">
                        <option v-for="n of networks[testnet ? 'testnet' : 'mainnet']" :value="n.id">{{ `${n.name} - ${n.alias}` }}</option>
                    </v-select>
                    <v-input label="Contract" class="col-span-2" v-model="modalLink.contract" />
                    <v-input label="Withdrawal Multiplier" v-model="modalLink.withdrawalMultiplier" />
                    <v-toggle label="Is Default" v-model="modalLink.isDefault" />
                    <v-toggle label="Is Token" v-model="modalLink.isToken" />
                    <v-toggle label="Allow Deposit" v-model="modalLink.allowDeposit" />
                    <v-toggle label="Allow Withdrawal" v-model="modalLink.allowWithdrawal" />
                    <v-toggle label="Allow Crosschain Swap From" v-model="modalLink.allowCrosschainSwapFrom" />
                    <v-toggle label="Allow Crosschain Swap To" v-model="modalLink.allowCrosschainSwapTo" />
                    <v-toggle label="Allow Auto Swap From" v-model="modalLink.allowAutoSwapFrom" />
                    <v-toggle label="Allow Auto Swap To" v-model="modalLink.allowAutoSwapTo" />
                    <v-toggle label="Allow Crosschain Transfers" v-model="modalLink.allowCrosschainTransfers" />
                    <v-toggle label="Allow Recurring" v-model="modalLink.allowRecurring" />
                    <v-toggle label="Enable Crosschain" v-model="modalLink.enableCrosschain" />
                </div>
                <template v-slot:footer>
                    <v-button type="button" color="green" :disabled="loading" @click="saveLink">Save</v-button>
                </template>
            </v-modal>


        </div>

    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from '@/Layouts/Authenticated.vue';
import NavigationSettings from '@/Components/Navigation/Administration';
import moment from 'moment';
import { Head, Link } from '@inertiajs/inertia-vue3';
import VButton from '@/Components/Button';
import VButtonLink from '@/Components/ButtonLink';
import VInput from '@/Components/Input';
import VSelect from '@/Components/Select';
import VToggle from '@/Components/Toggle';
import VModal from '@/Components/Modal';


export default {
    components: {
        Head, Link,
        NavigationSettings,
        AuthenticatedLayout,
        VButton,
        VButtonLink,
        VInput,
        VSelect,
        VToggle,
        VModal,
    },
    data() {
        return {
            testnet: false,
            filters: {
                isActive: null,
                inBlockchain: null,
                isFiat: null,
            },
            sortBy: "orderIndex-asc",
            loading: false,
            showCurrencyModal: false,
            modalCurrency: {},
            showLinkModal: false,
            modalLink: {},
        }
    },
    props: {
        notifications: Array,
        currencies: Object,
        networks: Object,
    },
    methods: {
        moment,
        openCurrencyModal(currency = null) {
            this.showCurrencyModal = true;

            this.modalCurrency = currency
                ? JSON.parse(JSON.stringify(currency))
                : {
                    "currency": "",
                    "alias": "",
                    "isActive": false,
                    "inBlockchain": false,
                    "isBaseCurrency": false,
                    "isFiat": false,
                    "urlInfo": null,
                    "orderIndex": 0,
                    "system": false,
                    "stablecoin": false,
                    "selfBlockchain": false,
                    "exchangeEqual": null,
                    "allowDeposit": false,
                    "allowWithdraw": false,
                    "allowExchange": false,
                    "allowInvoice": false,
                    "allowPayout": false,
                    "allowBusiness": false,
                    "allowAccounts": false,
                    "rounding": 2,
                };

            this.modalCurrency.testnet = this.testnet;
        },
        openLinkModal(currency, network = null) {
            this.showLinkModal = true;

            this.modalCurrency = currency;
            this.modalLink = network ? network.currencies_networks : { currencyId: currency.id };
            this.modalLink.isCreate = !network;

            this.modalLink.testnet = this.testnet;
        },
        saveCurrency() {
            this.loading = true;
            const currency = this.modalCurrency;
            axios.post(currency.id
                        ? this.route('admin.currencies.update')
                        : this.route('admin.currencies.create')
                    , currency)
                .then(res => {
                    this.notifications.push(...res.data.notifications);
                    this.loading = false;

                    if (res.data.success) {
                        const resultCurrency = res.data.result;
                        const index = this.currenciesSelected.findIndex(c => c.id === resultCurrency.id);
                        if (index !== -1) {
                            this.currenciesSelected.splice(index, 1, resultCurrency);
                        } else {
                            this.currenciesSelected.push(resultCurrency);
                        }
                        this.showCurrencyModal = false;
                    }
                })
                .catch(err => {
                    this.loading = false;
                    if (err.response && err.response.data.errors) {
                        for (const [field, messages] of Object.entries(err.response.data.errors)) {
                            messages.forEach(message => this.notifications.push({
                                type: 'warning',
                                title: `Validation Error`,
                                message
                            }));
                        }
                    } else {
                        this.notifications.push({
                            type: 'error',
                            title: `Error ${err.response?.status || 'Unknown'}`,
                            message: err.response?.data.message || err.response?.data || 'Something went wrong'
                        });
                    }
                });
        },
        deleteCurrency(currency) {
            if(!window.confirm(`Delete ${currency.currency} (${currency.alias})?`))
                return false;

            const { id } = currency;
            const { testnet } = this;
            axios.post(this.route('admin.currencies.delete'), { id, testnet })
                .then(res => {
                    this.notifications.push(...res.data.notifications);
                    if (res.data.success) {
                        const key = testnet ? 'testnet' : 'mainnet';
                        this.currencies[key] = this.currencies[key].filter(item => item.id !== id);
                    }
                })
                .catch(err => {
                    this.notifications.push({
                        type: 'error',
                        title: `Error ${err.response?.status || 'Unknown'}`,
                        message: err.response?.data.message || err.response?.data || 'Something went wrong'
                    });
                });
        },
        saveLink() {
            this.loading = true;
            const link = this.modalLink;
            axios.post(link.isCreate
                    ? this.route('admin.currencies.link.create')
                    : this.route('admin.currencies.link.update')
                , link)
                .then(res => {
                    this.notifications.push(...res.data.notifications);
                    this.loading = false;

                    if (res.data.success) {
                        const updatedLink = res.data.result;

                        const currencyIndex = this.currenciesSelected.findIndex(
                            c => c.id === updatedLink.currencyId
                        );

                        if (currencyIndex !== -1) {
                            const currency = this.currenciesSelected[currencyIndex];

                            if (!currency.networks || !Array.isArray(currency.networks)) {
                                currency.networks = [];
                            }

                            const network = this.networks[this.testnet ? 'testnet' : 'mainnet']
                                .find(n => n.id === updatedLink.networkId);

                            const existNetworkIndex = currency.networks.findIndex(
                                n => n.id === updatedLink.networkId
                            );

                            if (existNetworkIndex !== -1) {
                                currency.networks[existNetworkIndex] = {
                                    ...network,
                                    currencies_networks: updatedLink,
                                };
                            } else {
                                currency.networks.push({
                                    ...network,
                                    currencies_networks: updatedLink,
                                });
                            }

                            this.currenciesSelected[currencyIndex] = currency;
                        }

                        this.showLinkModal = false;
                    }
                })
                .catch(err => {
                    console.log(err)
                    this.loading = false;
                    if (err.response && err.response.data.errors) {
                        for (const [field, messages] of Object.entries(err.response.data.errors)) {
                            messages.forEach(message => this.notifications.push({
                                type: 'warning',
                                title: `Validation Error`,
                                message
                            }));
                        }
                    } else {
                        this.notifications.push({
                            type: 'error',
                            title: `Error ${err.response?.status || 'Unknown'}`,
                            message: err.response?.data.message || err.response?.data || 'Something went wrong'
                        });
                    }
                });
        },
        deleteLink(currency, network) {
            if (!window.confirm(`Delete ${currency.currency} - ${network.name}?`))
                return false;

            axios.post(this.route('admin.currencies.link.delete'), {
                    currencyId: currency.id,
                    networkId: network.id,
                    testnet: this.testnet,
                })
                .then(res => {
                    this.notifications.push(...res.data.notifications);

                    if (res.data.success) {
                        const currencyIndex = this.currenciesSelected.findIndex(c => c.id === currency.id);

                        if (currencyIndex !== -1) {
                            const networks = this.currenciesSelected[currencyIndex].networks;

                            const networkIndex = networks.findIndex(n => n.id === network.id);
                            if (networkIndex !== -1) {
                                networks.splice(networkIndex, 1);

                                this.currenciesSelected[currencyIndex] = {
                                    ...this.currenciesSelected[currencyIndex],
                                    networks,
                                };
                            }
                        }
                    }
                })
                .catch(err => {
                    console.log(err)
                    this.notifications.push({
                        type: 'error',
                        title: `Error ${err.response?.status || 'Unknown'}`,
                        message: err.response?.data.message || err.response?.data || 'Something went wrong'
                    });
                });
        }

    },
    computed: {
        currenciesSelected() {
            return this.currencies[this.testnet ? 'testnet' : 'mainnet'];
        },
        currenciesFiltered() {
            let currencies = this.currenciesSelected;

            if (this.filters.isActive !== null)
                currencies = currencies.filter((currency) => currency.isActive === this.filters.isActive);

            if (this.filters.inBlockchain !== null)
                currencies = currencies.filter((currency) => currency.inBlockchain === this.filters.inBlockchain);

            if (this.filters.isFiat !== null)
                currencies = currencies.filter((currency) => currency.isFiat === this.filters.isFiat);

            const [key, direction] = this.sortBy.split("-");
            currencies = currencies.slice().sort((a, b) => {
                const valA = a[key];
                const valB = b[key];

                if (valA === valB) return 0;

                const isAsc = direction === "asc";
                return (valA > valB ? 1 : -1) * (isAsc ? 1 : -1);
            });

            return currencies;
        },
    },
}
</script>
